import { Dropdown } from "primereact/dropdown";
import {
  useManagedMedicalCenters,
  useMedicalCenterClientRequirementsList,
  useSingleMedicalCase,
} from "../../hooks/queries";
import { useContext, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { reassignMedicalCase } from "../../services/medicalCases";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { ToastContext } from "../../ToastContext";
import Dashboard from "../../Layout/Dashboard";
import { useNavigate, useParams } from "react-router-dom";
import { ProgressSpinner } from "primereact/progressspinner";
import FormSubHeading from "../../components/FormSubHeading";
import { Checkbox } from "primereact/checkbox";
import { Divider } from "primereact/divider";

/**
 * Page to reassign a case by a sub-administrator to another provider
 */
export default function ReassignCase() {
  // Buscar detalle del caso
  const { caseId } = useParams();
  const { data, isLoading } = useSingleMedicalCase(caseId);

  const [assignedCenters, setAssignedCenters] = useState([]);
  const [showDialog, setShowDialog] = useState(false);

  const managedCenters = useManagedMedicalCenters();

  const [selectedCenter, setSelectedCenter] = useState(null);
  const [selectedRequirements, setSelectedRequirements] = useState([]);

  // Filtrar los requerimientos no asignados a algún centro
  const unassignedRequirements = data?.requirements.filter(
    (requirement) =>
      !assignedCenters.some((assignedRequirement) =>
        assignedRequirement.requirements.includes(requirement.requirement)
      )
  );

  // Obtener requerimientos disponibles para el centro seleccionado
  const availableRequirements = useMedicalCenterClientRequirementsList({
    centerId: selectedCenter?.id,
    clientId: null,
    subAdministrator: data?.medical_center,
  });

  // Requerimientos libres y disponibles para el centro seleccionado
  const requirementsForListing = unassignedRequirements?.filter((requirement) =>
    availableRequirements.data?.some(
      (availableRequirement) =>
        availableRequirement.requirement === requirement.requirement
    )
  );

  const assignMedicalCenter = () => {
    if (selectedCenter && selectedRequirements.length > 0) {
      setAssignedCenters((prev) => {
        const existingCenter = prev.find(
          (center) => center.medicalCenter === selectedCenter.id
        );

        if (existingCenter) {
          return prev.map((center) =>
            center.medicalCenter === selectedCenter.id
              ? {
                  ...center,
                  requirements: Array.from(
                    new Set([...center.requirements, ...selectedRequirements])
                  ),
                }
              : center
          );
        } else {
          return [
            ...prev,
            {
              medicalCenter: selectedCenter.id,
              requirements: selectedRequirements,
            },
          ];
        }
      });

      setShowDialog(false);
      setSelectedCenter(null);
      setSelectedRequirements([]);
    }
  };

  const toast = useContext(ToastContext);
  const navigate = useNavigate();

  const queryClient = useQueryClient();
  const assignCaseMutation = useMutation({
    mutationFn: reassignMedicalCase,
    onSuccess: ({ type, message }) => {
      if (type === "success") {
        queryClient.invalidateQueries({
          queryKey: [["medicalCase", Number(caseId)]],
        });
        toast?.current.show({
          severity: "success",
          summary: "Éxito",
          detail: "Caso asignado con éxito",
        });
        navigate(`/casos/${data.id}`);
      } else {
        toast?.current.show({
          severity: "error",
          summary: "Error",
          detail: message,
        });
      }
    },
  });

  const saveChanges = () =>
    assignCaseMutation.mutate({
      caseId: data.id,
      assigned: assignedCenters.map((center) => ({
        medical_center: center.medicalCenter,
        requirements: center.requirements.map((r) => ({ requirement: r })),
      })),
    });

  return (
    <Dashboard headerTitle="Reasignar caso" dynamicGoBack>
      {(isLoading || !data) && <ProgressSpinner aria-label="Cargando" />}
      {data && (
        <div className="p-fluid">
          <h3>
            <i className="pi pi-ticket"></i> Número de Orden: {data.id}
          </h3>
          {assignedCenters.length === 0 && (
            <div className="w-5 pb-2 mx-auto text-center">
              <div className="text-left">
                <p>
                  <b>No has asignado ningún requerimiento a un proveedor.</b>
                </p>
                <p>Requerimientos pendientes:</p>
                <ul className="list-disc pl-3">
                  {unassignedRequirements.map((req) => (
                    <li key={req.requirement} className="mb-2">
                      {req.requirement_name}
                    </li>
                  ))}
                </ul>
              </div>
              <Button
                label="Añadir proveedor"
                icon="pi pi-plus"
                className="w-fit"
                onClick={() => setShowDialog(true)}
              />
            </div>
          )}
          {assignedCenters.length > 0 && (
            <>
              <div>
                <FormSubHeading title="Proveedores asignados" />
                {assignedCenters.map((center) => (
                  <div key={center.medicalCenter}>
                    <h4>
                      <i className="pi pi-building-columns"></i>
                      {"  "}
                      {
                        managedCenters.data?.find(
                          (c) => c.id === center.medicalCenter
                        )?.name
                      }
                    </h4>
                    <ul>
                      {center.requirements.map((req) => (
                        <li key={req}>
                          {
                            data?.requirements.find(
                              (r) => r.requirement === req
                            )?.requirement_name
                          }
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
              <Divider />
              {unassignedRequirements.length > 0 && (
                <>
                  <p>Requerimientos pendientes:</p>
                  <ul className="list-disc pl-3">
                    {unassignedRequirements.map((req) => (
                      <li key={req.requirement} className="mb-2">
                        {req.requirement_name}
                      </li>
                    ))}
                  </ul>
                  <Button
                    label="Añadir proveedor"
                    icon="pi pi-plus"
                    className="w-fit"
                    onClick={() => setShowDialog(true)}
                  />
                </>
              )}
              <div className="flex justify-content-end">
                <Button
                  label="Confirmar reasignación"
                  icon="pi pi-save"
                  severity="success"
                  className="w-fit mb-2"
                  disabled={
                    unassignedRequirements.length > 0 ||
                    assignCaseMutation.isPending
                  }
                  onClick={saveChanges}
                  tooltip="Asigna todos los requerimientos"
                  tooltipOptions={{ position: "top", showOnDisabled: true }}
                />
              </div>
            </>
          )}
        </div>
      )}

      <Dialog
        visible={showDialog}
        onHide={() => {
          if (showDialog) setShowDialog(false);
        }}
        header="Añadir proveedor"
        draggable={false}
        className="md:w-8"
      >
        <div className="p-fluid">
          <div className="pb-3">
            <FormSubHeading title="Selecciona un proveedor" />
            <Dropdown
              value={selectedCenter}
              onChange={(e) => setSelectedCenter(e.value)}
              options={managedCenters.data}
              loading={managedCenters.isLoading}
              optionLabel="name"
              placeholder="Seleccionar proveedor"
              filter
              filterBy="name"
              filterPlaceholder="Buscar"
              emptyMessage="No se encontraron proveedores"
              emptyFilterMessage="No se encontraron proveedores"
            />
          </div>

          <FormSubHeading title="Indica los requerimientos asignados a este proveedor" />
          <div className="flex flex-column gap-2 pl-2 pt-2 pb-3">
            {requirementsForListing?.map((req) => (
              <div key={req.requirement} className="flex align-items-center">
                <Checkbox
                  inputId={req.requirement}
                  value={req.requirement}
                  checked={selectedRequirements.some(
                    (r) => r === req.requirement
                  )}
                  onChange={(e) => {
                    if (e.checked) {
                      setSelectedRequirements((prev) => [
                        ...prev,
                        req.requirement,
                      ]);
                    } else {
                      setSelectedRequirements((prev) =>
                        prev.filter((r) => r !== req.requirement)
                      );
                    }
                  }}
                />
                <label
                  htmlFor={req.requirement}
                  className="ml-2 cursor-pointer"
                >
                  {req.requirement_name}
                </label>
              </div>
            ))}
          </div>
          <div className="flex justify-content-end">
            <Button
              label="Guardar asignación"
              icon="pi pi-save"
              className="w-fit"
              disabled={selectedRequirements.length === 0 || !selectedCenter}
              tooltip="Selecciona al menos un requerimiento"
              tooltipOptions={{ position: "top", showOnDisabled: true }}
              onClick={assignMedicalCenter}
            />
          </div>
        </div>
      </Dialog>
    </Dashboard>
  );
}
